import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Copy, DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { AuthContext } from '../App';
import LoveLinkEarningsPanel from '../components/LoveLinkEarningsPanel.tsx';
import { DEFAULT_REFERRALS_CONFIG, fetchReferralsConfig, formatPercent } from '../utils/referralsConfig';
import * as LoveLinksCopy from '../config/lovelinksCopy';

// Referrals config type
interface ReferralsConfig {
  l1_percent: number;
  l1_duration: string;
  l2_percent: number;
  l2_duration_days: number;
  l2_duration_label: string;
  l2_eligible_skus: string[];
  l2_eligible_product_rule: string;
}

interface DashboardData {
  referralLink: string | null;
  referralCode: string | null;
  lovelinkStatus: 'active' | 'paused';
  riskScore: number;
  totalEarned: number;
  pendingBalance: number;
  availableBalance: number;
  paidOut: number;
  level1Count: number;
  level2Count: number;
  minPayout: number;
  recentCommissions: Commission[];
}

interface CircleData {
  ok: boolean;
  invited_total: number;
  invited_paid_total: number;
  invited_recent: Array<{
    user_id: string;
    created_at: string;
    is_paid: boolean;
  }>;
}

interface Commission {
  id: string;
  createdAt: string;
  level: number;
  sourceType: string;
  plan: string;
  baseAmount: number;
  commissionAmount: number;
  status: string;
}

export default function ReferralDashboard() {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState<DashboardData | null>(null);
  const [circleData, setCircleData] = useState<CircleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Referrals config from backend (single source of truth)
  const [refConfig, setRefConfig] = useState<ReferralsConfig>(DEFAULT_REFERRALS_CONFIG as ReferralsConfig);
  
  // Tab state for dashboard sections
  const [activeTab, setActiveTab] = useState<'overview' | 'earnings' | 'referrals' | 'how-it-works'>('overview');
  
  // Payout request state
  const [payoutMethod, setPayoutMethod] = useState<'CASHAPP' | 'PAYPAL'>('CASHAPP');
  const [payoutHandle, setPayoutHandle] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState('');
  const [payoutError, setPayoutError] = useState('');
  
  // Status toggle state
  const [statusLoading, setStatusLoading] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  
  // Derived display values from config
  const l1PctLabel = formatPercent(refConfig.l1_percent);
  const l2PctLabel = formatPercent(refConfig.l2_percent);
  const l2DurationLabel = refConfig.l2_duration_label || "12 months";

  // Fetch referrals config on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      const cfg = await fetchReferralsConfig();
      if (mounted && cfg) setRefConfig(cfg as ReferralsConfig);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (token) {
      loadDashboard();
      loadCircleData();
    }
  }, [token]);

  const loadCircleData = async () => {
    if (!token) return;
    try {
      const response = await axios.get('/api/ref/circle/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircleData(response.data);
    } catch (err) {
      console.warn('Failed to load circle data:', err);
    }
  };

  const loadDashboard = async () => {
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/api/referrals/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (!data?.referralLink) return;

    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(data.referralLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.warn('Clipboard API failed, using fallback:', err);
          copyToClipboardFallback(data.referralLink);
        });
    } else {
      // Fallback for older browsers or blocked Clipboard API
      copyToClipboardFallback(data.referralLink);
    }
  };

  const copyToClipboardFallback = (text: string) => {
    // Create temporary textarea
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    
    // Select and copy
    textarea.focus();
    textarea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Fallback copy failed');
      }
    } catch (err) {
      console.error('Fallback copy error:', err);
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handlePayoutRequest = async () => {
    if (!payoutHandle.trim()) {
      setPayoutError('Please enter your payment handle');
      return;
    }

    if (!token) {
      setPayoutError('Not authenticated');
      return;
    }

    try {
      setPayoutLoading(true);
      setPayoutError('');
      setPayoutSuccess('');

      const response = await axios.post('/api/referrals/payout-request', {
        method: payoutMethod,
        details: { handle: payoutHandle }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPayoutSuccess(response.data.message);
      setPayoutHandle('');
      
      // Reload dashboard to show updated balances
      setTimeout(() => {
        loadDashboard();
      }, 1000);
    } catch (err: any) {
      setPayoutError(err.response?.data?.detail || 'Failed to submit payout request');
    } finally {
      setPayoutLoading(false);
    }
  };

  // Toggle LoveLink status
  const handleStatusToggle = async () => {
    if (!data || !token) return;
    
    const newStatus = data.lovelinkStatus === 'active' ? 'paused' : 'active';
    
    try {
      setStatusLoading(true);
      await axios.post('/api/referrals/status', {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setData(prev => prev ? { ...prev, lovelinkStatus: newStatus } : prev);
      setShowStatusConfirm(false);
    } catch (err: any) {
      console.error('Failed to toggle status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      AVAILABLE: 'bg-green-100 text-green-800',
      PAID: 'bg-blue-100 text-blue-800',
      REVERSED: 'bg-red-100 text-red-800',
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center surface-page">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center surface-page px-4">
        <div className="surface-card rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadDashboard}
            className="bg-rose-600 text-white px-6 py-2 rounded-full hover:bg-rose-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const canRequestPayout = data.availableBalance >= data.minPayout && payoutHandle.trim().length > 0;

  return (
    <div className="min-h-screen surface-page py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center text-sm font-medium text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 mb-4 transition-colors"
        >
          <span className="mr-1">←</span>
          Back to dashboard
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">Love Links</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {LoveLinksCopy.HEADLINE}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 dark:border-slate-700 pb-4">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'earnings', label: 'Rewards' },
            { id: 'referrals', label: 'My Circle' },
            { id: 'how-it-works', label: 'How Payments Work' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-rose-600 text-white shadow-md'
                  : 'surface-card text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Explanation Block */}
            <div className="surface-card rounded-xl shadow-md p-6 mb-8 border-l-4 border-rose-400">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line mb-4">
                {LoveLinksCopy.EXPLANATION}
              </p>
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">How Value Is Generated</h4>
                <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span>{LoveLinksCopy.HOW_VALUE_WORKS.step1}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>{LoveLinksCopy.HOW_VALUE_WORKS.step2}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-rose-100 text-rose-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    <span>{LoveLinksCopy.HOW_VALUE_WORKS.step3}</span>
                  </li>
                </ol>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic">
                  {LoveLinksCopy.HOW_VALUE_WORKS.footer}
                </p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Earned */}
          <div className="surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Total LoveLink Rewards</span>
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">${data.totalEarned.toFixed(2)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">All-time LoveLink Rewards from your Circle</div>
          </div>

          {/* Pending */}
          <div className="surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Pending Rewards</span>
              <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">${data.pendingBalance.toFixed(2)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">In 30-day review hold</div>
          </div>

          {/* Available */}
          <div className="surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Available Now</span>
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">${data.availableBalance.toFixed(2)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">LoveLink Rewards ready for payout</div>
          </div>

          {/* Paid Out */}
          <div className="surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Paid Out</span>
              <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">${data.paidOut.toFixed(2)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">LoveLink Rewards already paid out</div>
          </div>
        </div>

            {/* Quick Link Share */}
            <div className="surface-card rounded-xl shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Share Your Love Link</h2>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={data?.referralLink || ''}
                  readOnly
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg text-sm text-gray-600 dark:text-gray-300"
                />
                <button
                  onClick={copyReferralLink}
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition flex items-center gap-2"
                >
                  {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-2 block">
                {LoveLinksCopy.SHARE_MICROCOPY}
              </span>
            </div>
          </>
        )}

        {/* Earnings Tab */}
        {activeTab === 'earnings' && (
          <div className="space-y-8">
            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="surface-card rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Pending</span>
                  <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">${data.pendingBalance.toFixed(2)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">In 30-day review hold</div>
              </div>
              <div className="surface-card rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Available</span>
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">${data.availableBalance.toFixed(2)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ready for payout</div>
              </div>
              <div className="surface-card rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Paid Out</span>
                  <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">${data.paidOut.toFixed(2)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Already paid out</div>
              </div>
            </div>

            {/* LoveLink Earnings Panel */}
            <LoveLinkEarningsPanel />
          </div>
        )}

        {/* Referrals/My Circle Tab */}
        {activeTab === 'referrals' && (
          <>
        {/* My Circle Section - Joined vs Paid */}
        <div className="surface-card rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">👥 My Circle</h2>
          
          {circleData ? (
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/* Joined Counter */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 text-center">
                  <div className="text-4xl font-bold text-purple-600 dark:text-purple-400">{circleData.invited_total}</div>
                  <div className="text-sm text-purple-700 dark:text-purple-300 font-medium">Joined</div>
                  <div className="text-xs text-purple-500 dark:text-purple-400 mt-1">Total members in your Circle</div>
                </div>
                
                {/* Paid Counter */}
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                  <div className="text-4xl font-bold text-green-600 dark:text-green-400">{circleData.invited_paid_total}</div>
                  <div className="text-sm text-green-700 dark:text-green-300 font-medium">Elite</div>
                  <div className="text-xs text-green-500 dark:text-green-400 mt-1">Upgraded to Elite membership</div>
                </div>
              </div>

              {/* Empty states */}
              {circleData.invited_total === 0 ? (
                <div className="text-center py-6 bg-gray-50 dark:bg-slate-800 rounded-xl">
                  <div className="text-2xl mb-2">🌱</div>
                  <p className="text-gray-600 dark:text-gray-300">No one has joined from your link yet.</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Share your LoveLink to start building your Circle!</p>
                </div>
              ) : circleData.invited_paid_total === 0 ? (
                <div className="text-center py-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
                  <div className="text-xl mb-1">💫</div>
                  <p className="text-amber-700 dark:text-amber-300">You&apos;ve got {circleData.invited_total} member{circleData.invited_total > 1 ? 's' : ''} in your Circle!</p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">They haven&apos;t upgraded to Elite yet — you&apos;ll earn rewards when they do.</p>
                </div>
              ) : null}

              {/* Recent members */}
              {circleData.invited_recent.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Recent members</div>
                  <div className="space-y-2">
                    {circleData.invited_recent.slice(0, 5).map((member, idx) => (
                      <div key={member.user_id || idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-xs">
                            👤
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'Recently joined'}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          member.is_paid 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {member.is_paid ? '✨ Elite' : 'Free'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Referral lock notice */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>🔒 Attribution locked:</strong> When someone registers through your link, they&apos;re permanently in your Circle — even if they upgrade later.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="animate-pulse">Loading your Circle...</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* LoveLink Card */}
          <div className="lg:col-span-2 surface-card rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Your LoveLink</h2>
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  data.lovelinkStatus === 'active' 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    data.lovelinkStatus === 'active' ? 'bg-green-500' : 'bg-gray-400'
                  }`} />
                  {data.lovelinkStatus === 'active' ? 'Active' : 'Paused'}
                </span>
                <button
                  onClick={() => setShowStatusConfirm(true)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                  data-testid="lovelink-status-toggle-btn"
                >
                  {data.lovelinkStatus === 'active' ? 'Pause' : 'Activate'}
                </button>
              </div>
            </div>
            
            {/* Status Confirm Dialog */}
            {showStatusConfirm && (
              <div className="mb-4 p-4 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                  {data.lovelinkStatus === 'active' 
                    ? 'Pause this LoveLink? Your link will still work, but you won\'t be visible in growth stats.'
                    : 'Activate this LoveLink? Your link will be fully visible and earning enabled.'
                  }
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleStatusToggle}
                    disabled={statusLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      data.lovelinkStatus === 'active'
                        ? 'bg-gray-600 hover:bg-gray-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                    data-testid="lovelink-status-confirm-btn"
                  >
                    {statusLoading ? 'Updating...' : (data.lovelinkStatus === 'active' ? 'Yes, Pause' : 'Yes, Activate')}
                  </button>
                  <button
                    onClick={() => setShowStatusConfirm(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition"
                    data-testid="lovelink-status-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {data.referralLink ? (
              <div>
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <code className="text-sm text-gray-700 dark:text-gray-300 break-all">{data.referralLink}</code>
                    <button
                      onClick={copyReferralLink}
                      className="ml-4 flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Copy className="h-5 w-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg p-6 text-white">
                  <h3 className="font-bold text-lg mb-2">How LoveLink Works</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="mr-2">💰</span>
                      <span>Earn LoveLink Rewards ({l1PctLabel}) whenever someone in your Circle upgrades to Elite.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">⏱️</span>
                      <span>Rewards are held for 30 days for safety and fraud protection.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">💵</span>
                      <span>Request payout of your LoveLink Rewards anytime after reaching the $20 minimum.</span>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">Generating your LoveLink...</p>
            )}
          </div>

          {/* Circle & LoveLink Levels */}
          <div className="surface-card rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Your Circle</h2>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Primary Connections (Circle 1)</span>
                  <span className="font-bold text-gray-900 dark:text-gray-100">{data.level1Count}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">People you directly invited. {l1PctLabel} of qualifying purchases, for life.</div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Extended Connections (Circle 2)</span>
                  <span className="font-bold text-gray-900 dark:text-gray-100">{data.level2Count}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">People invited by your Circle. {l2PctLabel} of monthly subscriptions, for {l2DurationLabel}.</div>
              </div>
            </div>
          </div>
        </div>

        {/* LoveLink Payout Request Card */}
        <div className="surface-card rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Request LoveLink Payout</h2>
          
          {payoutSuccess && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-green-900 dark:text-green-200">LoveLink Payout Requested!</p>
                <p className="text-sm text-green-700 dark:text-green-300">{payoutSuccess}</p>
              </div>
            </div>
          )}

          {payoutError && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900 dark:text-red-200">Error</p>
                <p className="text-sm text-red-700 dark:text-red-300">{payoutError}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Payment Method
              </label>
              <select
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value as 'CASHAPP' | 'PAYPAL')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              >
                <option value="CASHAPP">Cash App</option>
                <option value="PAYPAL">PayPal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {payoutMethod === 'CASHAPP' ? 'Cash App Tag' : 'PayPal Email'}
              </label>
              <input
                type="text"
                value={payoutHandle}
                onChange={(e) => setPayoutHandle(e.target.value)}
                placeholder={payoutMethod === 'CASHAPP' ? '$YourCashTag' : 'your@email.com'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {data.availableBalance < data.minPayout ? (
                <span className="text-yellow-600 font-medium">
                  You need at least ${data.minPayout.toFixed(2)} in LoveLink Rewards (Currently: ${data.availableBalance.toFixed(2)}).
                </span>
              ) : (
                <span className="text-green-600 font-medium">
                  Ready for payout: ${data.availableBalance.toFixed(2)} in LoveLink Rewards.
                </span>
              )}
            </div>
            
            <button
              onClick={handlePayoutRequest}
              disabled={!canRequestPayout || payoutLoading}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                canRequestPayout && !payoutLoading
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {payoutLoading ? 'Processing LoveLink Payout...' : 'Request LoveLink Payout'}
            </button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            * LoveLink Rewards payouts are processed within 5–7 business days and are subject to fraud verification. Rewards have a 30-day hold period before they become available.
          </p>
        </div>

        {/* How Payouts Work Section */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl shadow-md p-6 mb-8 border border-purple-100 dark:border-purple-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <span>💡</span> How LoveLink Payouts Work
          </h2>
          
          {/* Explanation Block */}
          <div className="bg-white/80 dark:bg-slate-800/80 rounded-lg p-4 mb-6 border-l-4 border-rose-300 dark:border-rose-500">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-sm">
              {LoveLinksCopy.EXPLANATION}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Factual Details - De-emphasized */}
            <div className="bg-white/80 dark:bg-slate-800/80 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <span className="text-purple-600 dark:text-purple-400">📋</span> Factual Details
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-4">{LoveLinksCopy.FACTUAL_HEADER}</p>
              
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <div className="p-3 bg-purple-50/50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-700">
                  <div className="font-medium text-purple-800 dark:text-purple-300 mb-1">{LoveLinksCopy.LEVEL_LABELS.l1.title}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">{l1PctLabel}</span>
                    <span className="text-gray-600 dark:text-gray-400 text-xs">{LoveLinksCopy.LEVEL_LABELS.l1.description}</span>
                  </div>
                  <div className="text-xs text-purple-500 dark:text-purple-400 mt-1">{LoveLinksCopy.LEVEL_LABELS.l1.duration}</div>
                </div>
                <div className="p-3 bg-blue-50/50 dark:bg-blue-900/30 rounded-lg border border-blue-100 dark:border-blue-700">
                  <div className="font-medium text-blue-800 dark:text-blue-300 mb-1">{LoveLinksCopy.LEVEL_LABELS.l2.title}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">{l2PctLabel}</span>
                    <span className="text-gray-600 dark:text-gray-400 text-xs">{LoveLinksCopy.LEVEL_LABELS.l2.description}</span>
                  </div>
                  <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">{LoveLinksCopy.LEVEL_LABELS.l2.duration}</div>
                </div>
              </div>
            </div>

            {/* Payout Process */}
            <div className="bg-white/80 dark:bg-slate-800/80 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <span className="text-green-600 dark:text-green-400">💸</span> Payout Process
              </h3>
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-start gap-2">
                  <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <p><strong>30-Day Hold:</strong> All rewards are held for 30 days to prevent fraud and ensure subscription validity.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <p><strong>Available Balance:</strong> After 30 days, rewards move to your "Available" balance for payout.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <p><strong>Request Payout:</strong> Once you have $20+, request a payout via Cash App or PayPal.</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <p><strong>Processing:</strong> Payouts are processed within 5-7 business days.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent LoveLink Rewards */}
        {data.recentCommissions.length > 0 && (
          <div className="surface-card rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Recent LoveLink Rewards</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Level</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Base</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">LoveLink Reward</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentCommissions.map((commission) => (
                    <tr key={commission.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(commission.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          commission.level === 1 ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          L{commission.level}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {commission.plan}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 text-right">
                        ${commission.baseAmount.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-green-600 text-right">
                        +${commission.commissionAmount.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusBadge(commission.status)}`}>
                          {commission.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </>
        )}

        {/* How Payments Work Tab */}
        {activeTab === 'how-it-works' && (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-md p-8 border border-purple-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>💡</span> How Love Links Payments Work
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Love Links is your personal referral program. When people join through your link and upgrade to Elite, 
                you earn rewards. Here&apos;s everything you need to know.
              </p>
            </div>

            {/* Referral Levels */}
            <div className="surface-card rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2 text-lg">
                <span className="text-purple-600 dark:text-purple-400">💰</span> Reward Structure
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                  <div className="font-semibold text-purple-800 dark:text-purple-300 mb-1">{LoveLinksCopy.LEVEL_LABELS.l1.title}</div>
                  <div className="text-xs text-purple-600/80 dark:text-purple-400 mb-2">{LoveLinksCopy.LEVEL_LABELS.l1.subtitle}</div>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{l1PctLabel}</div>
                  <div className="text-sm text-purple-700 dark:text-purple-300">
                    {LoveLinksCopy.LEVEL_LABELS.l1.description}
                  </div>
                  <div className="text-xs text-purple-500 dark:text-purple-400 mt-1">{LoveLinksCopy.LEVEL_LABELS.l1.duration}</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                  <div className="font-semibold text-blue-800 dark:text-blue-300 mb-1">{LoveLinksCopy.LEVEL_LABELS.l2.title}</div>
                  <div className="text-xs text-blue-600/80 dark:text-blue-400 mb-2">{LoveLinksCopy.LEVEL_LABELS.l2.subtitle}</div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{l2PctLabel}</div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    {LoveLinksCopy.LEVEL_LABELS.l2.description}
                  </div>
                  <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">{LoveLinksCopy.LEVEL_LABELS.l2.duration}</div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="surface-card rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2 text-lg">
                <span className="text-yellow-600 dark:text-yellow-400">⏱️</span> Payment Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-600 dark:text-yellow-400 font-bold">1</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">30-Day Hold Period</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      All rewards are held for 30 days for fraud protection. This ensures the subscription is valid and not disputed.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 dark:text-green-400 font-bold">2</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">Funds Become Available</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      After 30 days, your rewards move from &quot;Pending&quot; to &quot;Available&quot; and can be paid out.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 dark:text-blue-400 font-bold">3</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">Request Payout</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Once you have at least $20 available, you can request a payout via Cash App or PayPal. Payouts are processed within 5-7 business days.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl shadow-md p-6 border border-amber-200 dark:border-amber-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2 text-lg">
                <span>📝</span> Important Notes
              </h3>
              <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 dark:text-amber-400">•</span>
                  <span><strong>Permanent Attribution:</strong> When someone registers through your link, they&apos;re permanently in your Circle — even if they upgrade months later.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 dark:text-amber-400">•</span>
                  <span><strong>Recurring Rewards:</strong> You earn on both first-time subscriptions AND renewals from your Circle members.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 dark:text-amber-400">•</span>
                  <span><strong>No Caps:</strong> There&apos;s no limit to how much you can earn. Build your Circle and grow your rewards.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 dark:text-amber-400">•</span>
                  <span><strong>Fraud Protection:</strong> If a payment is refunded or disputed, the associated reward is reversed to protect the ecosystem.</span>
                </li>
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-xl shadow-md p-6 text-white text-center">
              <h3 className="font-bold text-xl mb-2">{LoveLinksCopy.CTA_TITLE}</h3>
              <p className="text-rose-100 mb-4">{LoveLinksCopy.CTA_BODY}</p>
              <button
                onClick={() => setActiveTab('overview')}
                className="bg-white dark:bg-slate-900 text-rose-600 dark:text-rose-400 px-6 py-2 rounded-full font-semibold hover:bg-rose-50 dark:hover:bg-slate-800 transition"
              >
                Get My Link →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
